import { useQuery } from "@tanstack/react-query";
import { Workflow } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { FlowSummary } from "@/api";
import { listFlowsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, byNewest, useListControls } from "@/components/build/useListControls";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { FlowIcon } from "./flowMeta";
import { useDeleteFlow } from "./useDeleteFlow";

/** "Mine" = any membership role (same as agents). */
const isMine = (f: FlowSummary) => Boolean(f.currentUserRole);

/** Build > Workflows list. */
export function FlowsListPage() {
  const { t } = useTranslation(["build", "workflows"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteFlow();
  const { search, apply, listProps } = useListControls<FlowSummary>({
    isMine,
    sorts: [
      { value: "name", label: t("build:list.sort.name"), compare: byName },
      { value: "newest", label: t("build:list.sort.newest"), compare: byNewest },
      { value: "runs", label: t("workflows:sortRuns"), compare: (a, b) => b.executionCount - a.executionCount },
    ],
  });

  const query = useQuery(listFlowsOptions({ query: { page_size: 50 } }));
  const flows = apply(query.data?.data);

  return (
    <EntityListPage
      title={t("workflows:title")}
      subtitle={t("workflows:subtitle")}
      newLabel={t("workflows:new")}
      onNew={() => navigate("/build/workflows/new")}
      headerActions={<BuildWithAiButton entity="workflow" />}
      {...listProps}
    >
      {flows.map((flow) => (
        <EntityRow
          key={flow.uuid}
          icon={<FlowIcon />}
          name={flow.name}
          description={flow.description}
          meta={
            <>
              <span className="text-content-tertiary">{t("workflows:steps", { count: flow.stepCount })}</span>
              {flow.hasActiveSchedule && <span className="text-content-tertiary">{t("workflows:scheduled")}</span>}
            </>
          }
          onOpen={() => navigate(`/build/workflows/${flow.uuid}`)}
          menuItems={[
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/workflows/${flow.uuid}/steps`) },
            { label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(flow) },
          ]}
        />
      ))}
      {flows.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState
            icon={<Workflow size={32} />}
            title={t("workflows:empty.title")}
            body={t("workflows:empty.body")}
          />
        ))}
      {dialog}
    </EntityListPage>
  );
}
